/**
 * Moving a recommendation in the Assessment Letter (LP-32).
 *
 * The recommendations are the only paragraphs of the letter that move,
 * and a move renumbers them. A move takes a recommendation to its new
 * place and then:
 *
 *   - a heading that leads with a number ("2. Driver evaluation",
 *     "2) ...", "2: ...", "Recommendation 2: ...") takes the number of
 *     its new position; a heading with no number is left as written;
 *   - a reference in the letter's prose that counts them by number
 *     ("recommendation 2", "Recommendation #2") follows the recommendation
 *     it pointed at, so the words still name the same recommendation after
 *     the move. The opening, the closing and every recommendation's own
 *     paragraphs, resources and steps are read.
 *
 * Pure: a new letter object every time, the input untouched. A move off
 * either end returns the letter as it was.
 */

import type { AssessmentLetterBody, AssessmentLetterRecommendation } from '@/lib/api';

/** A heading's leading ordinal: "2. ", "2) ", "2: ", "2 - ", or
 *  "Recommendation 2: " (the word in either case). The separator must be
 *  followed by a space, so a heading that only starts with a figure
 *  ("24-hour care", "2.5 hours of help") is not an ordinal. */
const HEADING_NUMBER = /^(\s*(?:recommendation\s+)?)(\d+)((?:\s*[.):]|\s+-)\s+)/i;

/** A prose reference that counts the recommendations: "recommendation 2",
 *  "Recommendation #2", "recommendation no. 2", "recommendations 2". */
const PROSE_REFERENCE = /\b(recommendations?\s+(?:#\s*|no\.\s*)?)(\d+)\b/gi;

/** The heading with its leading number set to `position` (1-based); a
 *  heading that carries no number is returned as it is. */
export function renumberHeading(heading: string, position: number): string {
  return heading.replace(HEADING_NUMBER, (_m, lead: string, _n: string, tail: string) => `${lead}${position}${tail}`);
}

/** The text with every "recommendation N" moved through `map` (old
 *  1-based number to new 1-based number); a number the map does not name
 *  is left as written. */
export function remapReferences(text: string, map: ReadonlyMap<number, number>): string {
  return text.replace(PROSE_REFERENCE, (m, lead: string, n: string) => {
    const next = map.get(Number(n));
    return next === undefined ? m : `${lead}${next}`;
  });
}

function remapRecommendation(
  rec: AssessmentLetterRecommendation,
  position: number,
  map: ReadonlyMap<number, number>,
): AssessmentLetterRecommendation {
  const re = (t: string): string => remapReferences(t, map);
  return {
    ...rec,
    heading: renumberHeading(re(rec.heading), position),
    paragraphs: rec.paragraphs.map(re),
    resources: rec.resources.map(re),
    steps: rec.steps.map(re),
  };
}

/**
 * The letter with the recommendation at `from` moved to `to` (both
 * 0-based), every numbered heading renumbered to its position, and every
 * numbered reference in the prose following its recommendation. The same
 * letter object comes back when either index is off the list or the two
 * are equal.
 */
export function moveRecommendation(letter: AssessmentLetterBody, from: number, to: number): AssessmentLetterBody {
  const n = letter.recommendations.length;
  if (from === to || from < 0 || to < 0 || from >= n || to >= n) return letter;

  const order = letter.recommendations.map((_r, i) => i);
  const [picked] = order.splice(from, 1);
  order.splice(to, 0, picked!);

  // Old 1-based number -> new 1-based number, for every recommendation.
  const map = new Map<number, number>();
  order.forEach((oldIndex, newIndex) => map.set(oldIndex + 1, newIndex + 1));
  const re = (t: string): string => remapReferences(t, map);

  return {
    ...letter,
    opening: letter.opening.map(re),
    closing: letter.closing.map(re),
    recommendations: order.map((oldIndex, newIndex) =>
      remapRecommendation(letter.recommendations[oldIndex]!, newIndex + 1, map),
    ),
  };
}
